/**
 * Sound Effects Module
 * Centralized sound management for the app
 * 
 * Features:
 * - Lazy loading: sounds load on first use
 * - iOS audio unlock: automatically unlocks audio context on first user interaction
 * - User preferences: sounds can be toggled on/off
 * - Mixes with background music: Uses Web Audio API to play sounds without interrupting music
 */

import { Capacitor } from '@capacitor/core'
import { playAlarmSound, isNativeAlarmAvailable } from './alarm-sound'

// Sound file paths
const SOUNDS = {
  explosion: '/sounds/Explosion PAS 61639.mp3',
  notification: '/sounds/Cold Freeze Winter.mp3',
  refreshComplete1: '/sounds/ES Short Stove Dry 01.mp3',
  refreshComplete2: '/sounds/ES Short Stove Dry 02.mp3',
  gems: '/sounds/Stones Gems Tip from Plastic Cup.mp3',
  // Welcome flow sounds
  welcome: '/sounds/Spell Cast Dispel Magic Short 02.mp3',
  gift: '/sounds/Holy Digital SciFi Chime.mp3',
  shardReveal: '/sounds/Potion of Healing.mp3',
  shield: '/sounds/Protective Energy Shield.mp3',
  fire: '/sounds/Small Flame 01 - Burst Ignition.mp3',
  // Ambient/loop sounds
  fireLoop: '/sounds/ES_Burning_Wood,_Bonfire,_Crispy,_Soft_Intensity,_Loop_Epidemic.mp3',
  // Long-press completion sounds
  longPressRumble: '/sounds/Very Short Tonal Rumble.mp3',
  longPressBell: '/sounds/Bells Handbell Call Bell.mp3',
  // Pomodoro timer completion sounds
  pomodoroShortBreakStart: '/sounds/focus/g_blow.mp3',      // Work done → short break
  pomodoroLongBreakStart: '/sounds/focus/gong.mp3',         // Work done → long break
  pomodoroFocusStart: '/sounds/focus/typewriter.mp3',       // Break done → back to focus
} as const

type SoundName = keyof typeof SOUNDS

// Cache for decoded audio buffers (Web Audio API)
const audioBufferCache: Map<SoundName, AudioBuffer> = new Map()

// Shared AudioContext for all sounds (allows mixing with background music)
let sharedAudioContext: AudioContext | null = null

// Track if audio has been unlocked (iOS requires user gesture)
let audioUnlocked = false
let initializationPromise: Promise<void> | null = null

// User preference for sound (stored in localStorage)
const SOUND_ENABLED_KEY = 'miac-sounds-enabled'

/**
 * Check if sounds are enabled
 */
export function isSoundEnabled(): boolean {
  if (typeof window === 'undefined') return false
  const stored = localStorage.getItem(SOUND_ENABLED_KEY)
  // Default to true if not set
  return stored === null ? true : stored === 'true'
}

/**
 * Enable or disable sounds
 */
export function setSoundEnabled(enabled: boolean): void {
  if (typeof window === 'undefined') return
  localStorage.setItem(SOUND_ENABLED_KEY, String(enabled))
}

/**
 * Toggle sound on/off
 */
export function toggleSound(): boolean {
  const newValue = !isSoundEnabled()
  setSoundEnabled(newValue)
  return newValue
}

/**
 * Get or create the shared audio context
 * Using a shared context allows all our sounds to mix with background music
 * instead of interrupting it (as HTMLAudioElement does on iOS)
 */
function getSharedAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null
  
  if (!sharedAudioContext) {
    const AudioContextClass = window.AudioContext || (window as unknown as { webkitAudioContext: typeof window.AudioContext }).webkitAudioContext
    if (AudioContextClass) {
      sharedAudioContext = new AudioContextClass()
    }
  }
  
  // Resume if suspended (iOS requirement after user gesture)
  if (sharedAudioContext?.state === 'suspended') {
    sharedAudioContext.resume()
  }
  
  return sharedAudioContext
}

/**
 * Unlock audio on iOS (must be called from user gesture)
 * iOS Safari/WKWebView requires user interaction before audio can play
 */
function unlockAudio(): void {
  if (audioUnlocked) return
  
  // Create the shared audio context and play a silent buffer to unlock
  try {
    const ctx = getSharedAudioContext()
    if (ctx) {
      // Create and play a silent buffer
      const buffer = ctx.createBuffer(1, 1, 22050)
      const source = ctx.createBufferSource()
      source.buffer = buffer
      source.connect(ctx.destination)
      source.start(0)
      
      // Resume context if suspended (iOS 12+)
      if (ctx.state === 'suspended') {
        ctx.resume()
      }
    }
    
    audioUnlocked = true
    console.debug('Audio unlocked successfully')
  } catch (error) {
    console.debug('Audio unlock attempt:', error)
  }
}

/**
 * Initialize sounds - sets up iOS audio unlock listener
 * Call this once when the app loads
 */
export function initSounds(): void {
  if (typeof window === 'undefined') return
  if (initializationPromise) return
  
  initializationPromise = new Promise((resolve) => {
    // On iOS, we need to unlock audio on first user interaction
    const isIOS = Capacitor.getPlatform() === 'ios'
    
    if (isIOS && !audioUnlocked) {
      const unlockHandler = () => {
        unlockAudio()
        // Remove listeners after first interaction
        document.removeEventListener('touchstart', unlockHandler)
        document.removeEventListener('touchend', unlockHandler)
        document.removeEventListener('click', unlockHandler)
        resolve()
      }
      
      document.addEventListener('touchstart', unlockHandler, { once: true, passive: true })
      document.addEventListener('touchend', unlockHandler, { once: true, passive: true })
      document.addEventListener('click', unlockHandler, { once: true, passive: true })
    } else {
      audioUnlocked = true
      resolve()
    }
  })
}

/**
 * Load and decode an audio buffer (lazy - only when first needed)
 */
async function loadAudioBuffer(name: SoundName): Promise<AudioBuffer | null> {
  if (audioBufferCache.has(name)) {
    return audioBufferCache.get(name)!
  }
  
  const ctx = getSharedAudioContext()
  if (!ctx) return null
  
  try {
    const response = await fetch(SOUNDS[name])
    const arrayBuffer = await response.arrayBuffer()
    const audioBuffer = await ctx.decodeAudioData(arrayBuffer)
    audioBufferCache.set(name, audioBuffer)
    return audioBuffer
  } catch (error) {
    console.debug(`Could not load audio buffer: ${name}`, error)
    return null
  }
}

/**
 * Preload all sounds eagerly (optional - for instant playback)
 */
export function preloadAllSounds(): void {
  Object.keys(SOUNDS).forEach((name) => {
    loadAudioBuffer(name as SoundName)
  })
}

/**
 * Play a sound by name using Web Audio API
 * This approach mixes with background music instead of interrupting it
 */
async function playSound(name: SoundName, volume = 0.7): Promise<void> {
  if (typeof window === 'undefined') return
  if (!isSoundEnabled()) return
  
  try {
    // Ensure audio is initialized (for iOS)
    if (!initializationPromise) {
      initSounds()
    }
    
    const ctx = getSharedAudioContext()
    if (!ctx) return
    
    // Resume context if suspended
    if (ctx.state === 'suspended') {
      await ctx.resume()
    }
    
    // Load the audio buffer (lazy load on first use)
    const buffer = await loadAudioBuffer(name)
    if (!buffer) return
    
    // Create a new source node for this playback
    // (source nodes can only be played once, so we create a new one each time)
    const source = ctx.createBufferSource()
    source.buffer = buffer
    
    // Create gain node for volume control
    const gainNode = ctx.createGain()
    gainNode.gain.value = volume
    
    // Connect: source -> gain -> destination
    source.connect(gainNode)
    gainNode.connect(ctx.destination)
    
    // Play the sound
    source.start(0)
  } catch (error) {
    // Silently fail - audio playback can fail for many reasons
    // (user hasn't interacted with page, browser policy, etc.)
    console.debug(`Could not play sound: ${name}`, error)
  }
}

// ============================================
// Specific sound functions for each use case
// All volumes reduced by 80% for subtle audio
// ============================================

/**
 * Play explosion sound (for streak explosions, celebrations)
 */
export function playExplosionSound(): void {
  playSound('explosion', 0.06)
}

/**
 * Play notification sound (for new notifications)
 */
export function playNotificationSound(): void {
  playSound('notification', 0.1)
}

/**
 * Play refresh complete sound (randomly picks between two variations)
 */
export function playRefreshCompleteSound(): void {
  const variant = Math.random() > 0.5 ? 'refreshComplete1' : 'refreshComplete2'
  playSound(variant, 0.05)
}

/**
 * Play gems/shard collection sound
 */
export function playGemsSound(): void {
  playSound('gems', 0.03)
}

// ============================================
// Welcome flow sounds
// ============================================

/**
 * Play welcome sound (for "Welcome to Miac" screen)
 */
export function playWelcomeSound(): void {
  playSound('welcome', 0.04)
}

/**
 * Play gift reveal sound
 */
export function playGiftSound(): void {
  playSound('gift', 0.04)
}

/**
 * Play shard/gem reveal sound (welcome flow)
 */
export function playShardRevealSound(): void {
  playSound('shardReveal', 0.04)
}

/**
 * Play shield reveal sound
 */
export function playShieldSound(): void {
  playSound('shield', 0.04)
}

/**
 * Play fire/streak sound
 */
export function playFireSound(): void {
  playSound('fire', 0.03)
}

// ============================================
// Looping sounds (ambient) - using shared Web Audio API context
// ============================================

// State for fire loop (uses shared audio context)
let fireLoopSource: AudioBufferSourceNode | null = null
let fireLoopGain: GainNode | null = null
let isFireLoopPlaying = false

/**
 * Start the fire/bonfire ambient loop (seamless using Web Audio API)
 * Uses the shared audio context to mix with background music
 * @param volume - Volume level (0-1), default 0.03 (very subtle background)
 */
export async function playFireLoop(volume = 0.03): Promise<void> {
  if (typeof window === 'undefined') return
  if (!isSoundEnabled()) return
  
  // Stop any existing loop first
  stopFireLoop()
  
  const ctx = getSharedAudioContext()
  if (!ctx) return
  
  isFireLoopPlaying = true
  
  try {
    // Load the buffer using the shared loading function
    const buffer = await loadAudioBuffer('fireLoop')
    if (!buffer || !isFireLoopPlaying) return // Check if stopped while loading
    
    // Create gain node for volume control and fading
    const gain = ctx.createGain()
    gain.gain.value = 0 // Start silent for fade in
    gain.connect(ctx.destination)
    fireLoopGain = gain
    
    // Create buffer source for seamless looping
    const source = ctx.createBufferSource()
    source.buffer = buffer
    source.loop = true
    source.connect(gain)
    fireLoopSource = source
    
    // Start playback
    source.start(0)
    
    // Fade in over 500ms
    gain.gain.linearRampToValueAtTime(volume, ctx.currentTime + 0.5)
  } catch (error) {
    console.debug('Could not play fire loop:', error)
    isFireLoopPlaying = false
  }
}

/**
 * Stop the fire/bonfire ambient loop immediately
 */
export function stopFireLoop(): void {
  isFireLoopPlaying = false
  
  const source = fireLoopSource
  const gain = fireLoopGain
  
  // Clear references immediately
  fireLoopSource = null
  fireLoopGain = null
  
  if (!source) return
  
  try {
    // Immediately stop - no fade for clean cleanup
    const ctx = getSharedAudioContext()
    if (gain && ctx) {
      gain.gain.cancelScheduledValues(ctx.currentTime)
      gain.gain.setValueAtTime(0, ctx.currentTime)
    }
    source.stop()
    source.disconnect()
    if (gain) gain.disconnect()
  } catch {
    // Already stopped or disconnected, ignore
  }
}

// ============================================
// Celebration sounds (completion dings)
// ============================================

/**
 * Play a subtle "ding" sound for habit completion
 * Uses synthesized tone that mixes with background music
 * @param type - 'gold' for full completion, 'silver' for partial, 'quick' for simple
 */
export function playCompletionDing(type: 'gold' | 'silver' | 'quick' = 'silver'): void {
  if (typeof window === 'undefined') return
  if (!isSoundEnabled()) return
  
  const ctx = getSharedAudioContext()
  if (!ctx) return
  
  try {
    // Resume context if suspended
    if (ctx.state === 'suspended') {
      ctx.resume()
    }
    
    // Different tones for different types
    const baseFreq = type === 'gold' ? 880 : type === 'quick' ? 660 : 550
    
    const oscillator = ctx.createOscillator()
    const gainNode = ctx.createGain()
    
    oscillator.connect(gainNode)
    gainNode.connect(ctx.destination)
    
    oscillator.frequency.setValueAtTime(baseFreq, ctx.currentTime)
    oscillator.type = 'sine'
    
    gainNode.gain.setValueAtTime(0.15, ctx.currentTime)
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5)
    
    oscillator.start(ctx.currentTime)
    oscillator.stop(ctx.currentTime + 0.5)
    
    // Add a second harmonic for gold
    if (type === 'gold') {
      const osc2 = ctx.createOscillator()
      const gain2 = ctx.createGain()
      
      osc2.connect(gain2)
      gain2.connect(ctx.destination)
      
      osc2.frequency.setValueAtTime(baseFreq * 1.5, ctx.currentTime + 0.1)
      osc2.type = 'sine'
      
      gain2.gain.setValueAtTime(0.1, ctx.currentTime + 0.1)
      gain2.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.6)
      
      osc2.start(ctx.currentTime + 0.1)
      osc2.stop(ctx.currentTime + 0.6)
    }
  } catch (error) {
    console.debug('Could not play completion ding:', error)
  }
}

// ============================================
// Long-press completion sounds
// ============================================

/**
 * Play the tonal rumble during long-press progress
 */
export function playLongPressRumble(): void {
  playSound('longPressRumble', 0.4)
}

/**
 * Play the bell chime when long-press completes
 */
export function playLongPressBell(): void {
  playSound('longPressBell', 0.12)
}

// ============================================
// Pomodoro Timer Sounds
// Uses native AVAudioPlayer on iOS for reliable alarm-like playback
// that works even when the mute/silent switch is on.
// Falls back to Web Audio API on web.
// ============================================

/**
 * Play sound when work session ends and short break is about to start
 * Sound: g_blow.wav - soft breath/wind sound
 */
export function playPomodoroShortBreakStartSound(): void {
  if (isNativeAlarmAvailable()) {
    playAlarmSound('g_blow.wav', 0.7)
  } else {
    playSound('pomodoroShortBreakStart', 0.5)
  }
}

/**
 * Play sound when work session ends and long break is about to start
 * Sound: gong.wav - deep gong sound for achievement
 */
export function playPomodoroLongBreakStartSound(): void {
  if (isNativeAlarmAvailable()) {
    playAlarmSound('gong.wav', 0.8)
  } else {
    playSound('pomodoroLongBreakStart', 0.6)
  }
}

/**
 * Play sound when break ends and user should start focusing again
 * Sound: typewriter.wav - crisp typewriter sound to signal work time
 */
export function playPomodoroFocusStartSound(): void {
  if (isNativeAlarmAvailable()) {
    playAlarmSound('typewriter.wav', 0.7)
  } else {
    playSound('pomodoroFocusStart', 0.5)
  }
}
